import { Api } from '../api/root-api';
import { updateUser } from '../auth';
import {
  CreateUserRequest,
  CreateUserResponse,
  InviteViaEmailResponse,
  InviteViaEmailRequest,
  ERPResponse,
  ERPRequest,
  ShareInviteResponse,
  ShareInviteRequest,
  CreateSellerRequest,
  GSTDetailsResponse,
  BusinessPlanDetailsResponse,
  PaymentStatusReq,
  PaymentStatusRes,
  QueryBizUserResponse,
  QueryBizUserRequest,
  TopUpRequest,
  TopUpResponse,
  UpgradePlanRequest,
  UpgradePlanResponse,
  SubscribePlanRequest,
  BusinessPlanDetailsRequest,
  SendAgreementDetailsRequest,
} from './model';

export const onboardingApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query(params) {
        return { url: `users`, method: 'PUT', body: params };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateUser(data));
        } catch (err) {
          console.log(err, 'dispatchErr');
        }
      },
    }),
    inviteViaEmail: builder.mutation<
      InviteViaEmailResponse,
      InviteViaEmailRequest
    >({
      query(params: InviteViaEmailRequest) {
        const { senderWorkspaceId } = params;
        return {
          url: `send/principalinvites/email/${senderWorkspaceId}`,
          method: 'POST',
          body: params,
        };
      },
    }),
    getGSTDetails: builder.query<any, string>({
      query(arg) {
        return { url: `/gst/${arg}`, method: 'GET' };
      },
    }),
    validateGSTDetails: builder.query<GSTDetailsResponse, string>({
      query(arg) {
        return { url: `/hub/orgs/api/gst/validate/${arg}`, method: 'GET' };
      },
    }),
    addErpDetails: builder.mutation<ERPResponse, ERPRequest>({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `erp-details/${workspaceId}`,
          method: 'POST',
          body: body,
        };
      },
    }),
    getShareInvite: builder.query<ShareInviteResponse, ShareInviteRequest>({
      query(arg) {
        return {
          url: `invite/${arg.customerId}`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    createSeller: builder.mutation<any, CreateSellerRequest>({
      query(arg) {
        return {
          url: 'seller/self-onboarding',
          method: 'POST',
          body: arg,
        };
      },
    }),
    getBusinessPlanDetails: builder.query<
      BusinessPlanDetailsResponse,
      BusinessPlanDetailsRequest
    >({
      query(args) {
        return {
          url: '/hub/rewards-vault/api/billing/plans',
          params: {
            sellerWorkspaceId: args.params.sellerWorkspaceId,
          },

          method: 'GET',
        };
      },
    }),
    sendAgreementDetails: builder.query<any, SendAgreementDetailsRequest>({
      query(args) {
        return {
          url: `/hub/rewards-vault/api/billing/agreement-acceptance/${args.params.sellerWorkspaceId}?sellerWorkspaceId=${args.params.sellerWorkspaceId}`,
          body: args.body,
          method: 'POST',
        };
      },
    }),

    getPaymentStatus: builder.query<PaymentStatusRes, PaymentStatusReq>({
      query(args) {
        const { hostedPageId } = args.params;
        return {
          url: `/hub/rewards-vault/api/billing/payment/status/${args.params.sellerWorkspaceId}/`,
          method: 'GET',
          params: args.params,
        };
      },
    }),
    getOnboardingStatus: builder.query<
      QueryBizUserResponse,
      QueryBizUserRequest
    >({
      query(arg) {
        return {
          url: 'https://app.usertour.io/graphql',
          method: 'POST',
          body: {
            query: `
              query queryBizUser($first: Int, $last: Int, $after: String, $before: String, $query: BizQuery!, $orderBy: BizOrder!) {
                queryBizUser(
                  first: $first
                  last: $last
                  after: $after
                  before: $before
                  query: $query
                  orderBy: $orderBy
                ) {
                  totalCount
                  edges {
                    cursor
                    node {
                      id
                      createdAt
                      externalId
                      environmentId
                      data
                    }
                  }
                  pageInfo {
                    startCursor
                    endCursor
                    hasNextPage
                    hasPreviousPage
                  }
                }
              }
            `,
            variables: {
              first: arg.first || 10,
              last: arg.last,
              after: arg.after,
              before: arg.before,
              query: arg.query,
              orderBy: arg.orderBy,
            },
          },
          headers: {
            accept: '*/*',
            'accept-language': 'en-US,en-GB;q=0.9,en;q=0.8',
            'content-type': 'application/json',
            origin: 'https://app.usertour.io',
            priority: 'u=1, i',
            referer:
              'https://app.usertour.io/env/cm93rer4908wcvqhtdt04q4xq/users',
            'sec-ch-ua':
              '"Chromium";v="134", "Not:A-Brand";v="24", "Google Chrome";v="134"',
            'sec-ch-ua-mobile': '?0',
            'sec-ch-ua-platform': '"Linux"',
            'sec-fetch-dest': 'empty',
            'sec-fetch-mode': 'cors',
            'sec-fetch-site': 'same-origin',
            'user-agent':
              'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.36',
          },
          credentials: 'include', // This tells fetch to include cookies in the request
        };
      },
    }),
    upgradePlan: builder.mutation<UpgradePlanResponse, UpgradePlanRequest>({
      query(args) {
        return {
          url: `/hub/rewards-vault/api/billing/updateSubscription/${args.sellerWorkspaceId}`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
    subscribe: builder.mutation<UpgradePlanResponse, SubscribePlanRequest>({
      query(args) {
        return {
          url: `/hub/rewards-vault/api/billing/subscription`,
          method: 'POST',
          body: args.body,
          params: args.params,
        };
      },
    }),
    topUp: builder.mutation<TopUpResponse, TopUpRequest>({
      query(args) {
        return {
          url: `/hub/rewards-vault/api/billing/topup/${args.params.sellerWorkspaceId}`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
  }),
});

export const {
  useCreateUserMutation,
  useInviteViaEmailMutation,
  useLazyGetGSTDetailsQuery,
  useLazyValidateGSTDetailsQuery,
  useAddErpDetailsMutation,
  useLazyGetShareInviteQuery,
  useCreateSellerMutation,
  useLazyGetBusinessPlanDetailsQuery,
  useLazyGetPaymentStatusQuery,
  useLazyGetOnboardingStatusQuery,
  useUpgradePlanMutation,
  useTopUpMutation,
  useLazySendAgreementDetailsQuery,
  useSubscribeMutation,
} = onboardingApi;
